import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import ChangeTransportList from '@/app/components/hotel-detail/changeTransport/ChangeTransportList';
import { EvoTransportCard } from '@/app/components/hotel-detail/evo/transport/EvoTransportCard';
import { Text } from '@lmn/design-system';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';
import { trackSelectNewTransport } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { useCreateAsyncPricing } from '@/app/components/hotel-detail/PricingProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { ChangeTransportDetailsModal } from '@/app/components/hotel-detail/changeTransport/ChangeTransportDetailsModal';
import { useChangeTransportHandler } from '@/app/components/hotel-detail/changeTransport/hooks/useChangeTransportHandler';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { BASE_PATH } from '@/app/components/search/utils/path';
import {
  isAnyFilterSelected,
  useTransportIsChangingFilters,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import {
  EVO_BODY_INSET,
  EVO_BODY_INSET_MEDIA,
  EVO_BODY_INSET_WIDE,
} from '@/app/components/search/components/Modal/OpenXModal/utils';
import { useChangeTransportModal } from '@/app/components/hotel-detail/evo/transport/EvoChangeTransportCard/ChangeTransportModalProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { EvoSelectedTransportCard } from '@/app/components/hotel-detail/evo/transport/EvoSelectedTransportCard';
import { logChangeTransportFailure } from '@/app/components/hotel-detail/changeTransport/logChangeTransportFailure';
import { EvoTransportFilters } from './EvoTransportFilters/EvoTransportFilters';

const StickyHeader = styled.div<{ $isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Bleed out of the modal body's horizontal inset so the sticky header spans the full modal
   * width; the modal body has no top padding of its own (EVO_BODY_PADDING_WIDE), so the header
   * already attaches to the modal title without exposing scrolled content above it. */
  margin: 0 -${EVO_BODY_INSET_WIDE};
  /* MPH-398 (mobile): no title heading here — keep the filters close to the modal title (8px) per
   * Figma, with 16px below so content scrolling under the sticky header keeps a gap (and the scroll
   * shadow stays visible). Desktop keeps its 24px top / 16px bottom (it has a heading above the pills). */
  padding: ${({ theme }) => theme.kallisto.space.space100} ${EVO_BODY_INSET_WIDE}
    ${({ theme }) => theme.kallisto.space.space200};
  /* MPH-1050 (mobile): the pills row has to scroll from one modal edge to the other instead of
   * being clipped short of them, so the header keeps no horizontal padding of its own — the 16px
   * inset is re-applied inside the scrolling row (see EvoTransportFilters). */
  @media ${EVO_BODY_INSET_MEDIA} {
    margin: 0 -${EVO_BODY_INSET};
    padding-left: 0;
    padding-right: 0;
  }
  @media ${customCss.breakpoints.lg} {
    /* MPH-832: once docked, the filters sit flush under the modal title bar, so the whole header
     * reads as the single 120px block Figma specifies (title 64 + pills 40 + 16 bottom) with one
     * shadow at its bottom edge. Unscrolled keeps the original 24px above the heading. */
    padding-top: ${(p) => (p.$isScrolled ? '0' : '24px')};
    padding-bottom: 16px;
  }
  border-bottom: 1px solid
    ${(p) => (p.$isScrolled ? p.theme.kallisto.color.border.static.borderNeutral : 'transparent')};
  box-shadow: ${(p) => (p.$isScrolled ? p.theme.kallisto.shadow.mediumDownShadow : 'none')};
  transition:
    box-shadow 150ms ease,
    border-color 150ms ease,
    padding-top 250ms ease;
`;

const HeaderTitle = styled.div<{ $hidden: boolean }>`
  overflow: hidden;
  max-height: ${(p) => (p.$hidden ? '0' : '80px')};
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  transform: translateY(${(p) => (p.$hidden ? '-8px' : '0')});
  /* Cancel the parent StickyHeader's 16px gap when collapsed so the filters
   * sit flush with the top once the title disappears. */
  margin-bottom: ${(p) => (p.$hidden ? '-16px' : '0')};
  transition:
    max-height 250ms ease,
    opacity 200ms ease,
    transform 250ms ease,
    margin-bottom 250ms ease;
`;

const TransportListContainer = styled.div`
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const FareChangeWarning = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space100};
  margin-bottom: ${({ theme }) => theme.kallisto.space.space150};
  padding: ${({ theme }) => theme.kallisto.space.space150};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundWarningSubtle};
`;

const FareChangeWarningIcon = styled(IconCircleInfoSolid)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconWarning};
`;

const NoFilteredResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  padding: ${({ theme }) => theme.kallisto.space.space300} 0;
  text-align: center;
`;

export default function EvoTransportPageLayout() {
  const { t } = useTranslation();
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);

  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return undefined;

    let scrollParent: HTMLElement | Window = window;
    let cur: HTMLElement | null = el.parentElement;
    while (cur) {
      const { overflowY } = getComputedStyle(cur);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParent = cur;
        break;
      }
      cur = cur.parentElement;
    }

    let lastScrolled: boolean | null = null;
    const update = () => {
      const top = scrollParent === window ? window.scrollY : (scrollParent as HTMLElement).scrollTop;
      const next = top > 0;
      if (next !== lastScrolled) {
        lastScrolled = next;
        setIsHeaderScrolled(next);
      }
    };

    update();
    scrollParent.addEventListener('scroll', update, { passive: true });
    return () => scrollParent.removeEventListener('scroll', update);
  }, []);

  const { data: asyncPricingData } = useCreateAsyncPricing();
  const { clearPreselectedTransport } = useOffersContext();
  const { data, mutate: mutateRooms, isValidating: roomsValidating } = useFetchRooms();
  const { data: selectedTransportData } = useSelectedTransport();
  const { setChanging, changingFilter } = useTransportIsChangingFilters();
  const roomsData = roomsValidating ? null : (data as RoomsServiceResponseType);
  const urlSearchParams = useUrlSearchParams();
  const initialParams = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { sendCuriosityClickEvent } = useCuriosity();
  const isDesktop = useIsLgOrGreater(!commonData.isMobileDevice);
  const {
    data: travelTransports,
    error: transportsFetchError,
    isFallbackTransport,
    isValidating: isValidatingTransports,
  } = useTransportsFetch();
  const transportResponse = travelTransports as FullAvailableTransportResponse | undefined;
  const noAvailableTransports = (transportResponse?.availableTransports?.length ?? 0) === 0;
  const totalMatches = transportResponse?.pageConfiguration?.filteredProducts ?? 0;
  const isLoadingTransports = !transportResponse && !transportsFetchError;
  const { currentFiltersStatus } = useCurrentFilterStatus();
  // MPH-1217: with filters selected, an empty list keeps the pills so the filters can be removed, and
  // says why there are no alternatives. Without filters it stays the plain selected-flight fallback.
  const isOverfiltered =
    !!transportResponse && !transportsFetchError && noAvailableTransports && isAnyFilterSelected(currentFiltersStatus);
  // While a filter change is refetching, the list on screen is still the previous (possibly empty) one,
  // so the pills must not depend on it until the new response arrives.
  const showFilters =
    isLoadingTransports ||
    isOverfiltered ||
    (isValidatingTransports && !!changingFilter) ||
    (!noAvailableTransports && !transportsFetchError);
  const pageTitleBase = urlSearchParams.isTrain
    ? t('vacation.oss.change_transport.page.title.train')
    : t('vacation.oss.change_transport.page.title.flight');
  const pageTitleMatches = t('vacation.oss.change_transport.page.title.matches', { count: String(totalMatches) });
  const pageTitle = isLoadingTransports ? pageTitleBase : `${pageTitleBase}: ${pageTitleMatches}`;

  const onChangeTransport = useCallback(
    (id: string, transportClass: string) => {
      setChanging(true);
      clearPreselectedTransport();
      sendCuriosityClickEvent({
        event_name: 'click_change_transport',
      });
      const changeTransportRequest: TravelChangeTransportRequest = {
        businessProfileId: localization.businessProfile,
        pricingId: asyncPricingData?.pricingId || '',
        pageType: initialParams.pageType,
        newTransportId: id || '',
        departureAirport: origin,
        transportClass,
        lang: localization.lang,
        locale: localization.locale,
        country: localization.country,
      };
      fetchWithTracingAndLogging(
        `${BASE_PATH}/api/hotel-detail/change-transport`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(changeTransportRequest),
        },
        getLoggingInfo(initialParams.pageType, 'travel-api-change-transport', 'change-transport'),
        searchInfoData.searchTraceParent,
        commonData
      )
        .then(() => {
          trackSelectNewTransport(urlSearchParams.isTrain);
          mutateRooms(roomsData ? { ...roomsData, changedTransport: true } : undefined);
        }, logChangeTransportFailure)
        .finally(() => setChanging(false));
    },
    [selectedTransportData?.transports[0]?.id]
  );

  const { selectedBrandedFare } = useSelectedMealPlan();
  const showFareChangeWarning = !!selectedBrandedFare && selectedBrandedFare.priceDifference !== 0;
  const { closeModal } = useChangeTransportModal();

  // MPH-468: after changing the flight the user must stay on the flight selection
  // page to review branded fares and flight details. onChangeTransport already
  // refetches rooms (changedTransport) and updates the selected flight on the
  // page, so we only need to close the modal instead of going to checkout.
  const onTransportSelected = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const { handleContinueClick } = useChangeTransportHandler(onChangeTransport, onTransportSelected);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        /* MPH-398: no gap here — the sticky header's own 16px padding-bottom is the only separation
           to the list (avoids doubling to 32px). */

        @media ${customCss.breakpoints.sm} {
          min-height: unset;
          margin: 0;
        }
      `}
    >
      {showFilters ? (
        <StickyHeader ref={stickyHeaderRef} $isScrolled={isHeaderScrolled}>
          {/* MPH-398 (mobile): title lives in the modal header ("Alternative flights"); desktop keeps the heading below */}
          {isDesktop && (
            <HeaderTitle $hidden={isHeaderScrolled} aria-hidden={isHeaderScrolled}>
              <Text variant="Title1">{pageTitle}</Text>
            </HeaderTitle>
          )}
          <EvoTransportFilters isDesktop={isDesktop ?? false} isLoading={isLoadingTransports} />
        </StickyHeader>
      ) : undefined}
      <TransportListContainer>
        {showFareChangeWarning && (
          <FareChangeWarning role="status">
            <FareChangeWarningIcon size={20} />
            {/* MPH-398: banner text is black, Body small Regular per Figma (icon stays warning-coloured) */}
            <Text variant="BodySmall" color="textPrimary">
              {t('vacation.oss.change_transport.fare_change_warning')}
            </Text>
          </FareChangeWarning>
        )}
        {isOverfiltered && (
          <NoFilteredResults role="status">
            <Text variant="Title4" color="textPrimary">
              {t('vacation.oss.noresults.title')}
            </Text>
            <Text variant="Body" color="textSecondary">
              {t('vacation.oss.noresults.subtitle_overfiltered')}
            </Text>
          </NoFilteredResults>
        )}
        {(isLoadingTransports || isFallbackTransport) && <EvoSelectedTransportCard />}
        <ChangeTransportList
          CardComponent={EvoTransportCard}
          evoStyle={true}
          onDirectSelect={(transportId, transportClass) => handleContinueClick({ transportId, transportClass })}
        />
        <ChangeTransportDetailsModal onChangeTransport={onChangeTransport} />
      </TransportListContainer>
    </div>
  );
}
